/**
 * ThemeCart — the only module that talks to the Shopify Cart API.
 *
 * Public events (dispatched on document):
 *   cart:updated  {detail: cart JSON}  emitted here after add, change, update, and refresh
 *   cart:open                          emitted by assets/cart-drawer.js
 *   cart:close                         emitted by assets/cart-drawer.js
 *
 * Requests are queued so rapid quantity changes cannot race.
 * Loaded before cart-drawer.js. The drawer will not add items without this file.
 */
(function () {
  'use strict';

  if (window.ThemeCart) return;

  var queue = Promise.resolve();

  function enqueue(task) {
    var run = queue.then(task, task);
    queue = run.then(function () {}, function () {});
    return run;
  }

  function routesRoot() {
    var routes = window.Shopify && window.Shopify.routes;
    var value = routes && routes.root ? routes.root : '/';
    return value.charAt(value.length - 1) === '/' ? value : value + '/';
  }

  function cartEndpoint(path) {
    return routesRoot() + path;
  }

  function sectionConfig() {
    var drawer = document.querySelector('[data-cart-drawer]');
    return {
      drawerId: (drawer && drawer.getAttribute('data-section-id')) || 'cart-drawer',
      headerId: (drawer && drawer.getAttribute('data-count-section')) || 'header'
    };
  }

  function readError(response, data) {
    var body = data || {};
    return new Error(body.description || body.message || response.statusText);
  }

  function postJSON(path, payload) {
    return fetch(cartEndpoint(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!response.ok) throw readError(response, data);
        return data;
      });
    });
  }

  function getNow() {
    return fetch(cartEndpoint('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.json().catch(function () {
        return {};
      }).then(function (data) {
        if (!response.ok) throw readError(response, data);
        return data;
      });
    });
  }

  function emitUpdated(cart) {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  }

  function copyCount(doc, selector, asHTML) {
    var source = doc.querySelector(selector);
    if (!source) return;
    var nodes = document.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (asHTML) nodes[i].innerHTML = source.innerHTML;
      else nodes[i].textContent = source.textContent;
      if (source.hasAttribute('data-count')) {
        nodes[i].setAttribute('data-count', source.getAttribute('data-count'));
      }
    }
  }

  function captureDrawerState() {
    var note = document.querySelector('[data-cart-drawer-note]');
    var agree = document.querySelector('[data-cart-drawer-agree]');
    var items = document.querySelector('[data-cart-drawer-items]');
    var dynamic = document.querySelector('[data-cart-drawer-dynamic]');
    var active = document.activeElement;
    return {
      note: note ? note.value : null,
      hadNote: !!note,
      noteFocused: !!(note && active === note),
      agree: agree ? agree.checked : false,
      hadAgree: !!agree,
      scrollTop: items ? items.scrollTop : 0,
      focusInside: !!(dynamic && active && dynamic.contains(active) && active !== note)
    };
  }

  function restoreDrawerState(state) {
    if (!state) return;
    var note = document.querySelector('[data-cart-drawer-note]');
    var agree = document.querySelector('[data-cart-drawer-agree]');
    var items = document.querySelector('[data-cart-drawer-items]');
    if (state.hadNote && note && state.note !== null) note.value = state.note;
    if (state.hadAgree && agree) agree.checked = state.agree;
    if (items) items.scrollTop = state.scrollTop;
    if (state.noteFocused && note) {
      note.focus();
    } else if (state.focusInside) {
      var panel = document.querySelector('.cart-drawer__panel');
      if (panel) panel.focus();
    }
  }

  function applySections(sections) {
    var ids = sectionConfig();
    var drawerHtml = sections && sections[ids.drawerId];
    if (drawerHtml) {
      var parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
      var next = parsed.querySelector('[data-cart-drawer-dynamic]');
      var current = document.querySelector('[data-cart-drawer-dynamic]');
      if (next && current) {
        var state = captureDrawerState();
        current.innerHTML = next.innerHTML;
        restoreDrawerState(state);
      }
    }

    var headerHtml = sections && sections[ids.headerId];
    if (headerHtml) {
      var headerDoc = new DOMParser().parseFromString(headerHtml, 'text/html');
      copyCount(headerDoc, '.cart-drawer-count', false);
      copyCount(headerDoc, '.cart-item-count-header--quantity', false);
      copyCount(headerDoc, '.cart-item-count-header--total', true);
    }

    try {
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        window.Shopify.PaymentButton.init();
      }
    } catch (error) {}
  }

  function refreshNow(cart) {
    var ids = sectionConfig();
    var url = routesRoot() + '?sections=' + ids.drawerId + ',' + ids.headerId;
    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    }).then(function (sections) {
      applySections(sections);
      if (cart) return emitUpdated(cart);
      return getNow().then(emitUpdated);
    });
  }

  function add(items) {
    return enqueue(function () {
      return postJSON('cart/add.js', { items: items }).then(function () {
        return refreshNow().catch(function () {
          return getNow().then(emitUpdated);
        });
      });
    });
  }

  function change(input) {
    return enqueue(function () {
      var payload = { quantity: input.quantity };
      if (input.line != null && input.line !== '') payload.line = input.line;
      else if (input.id != null && input.id !== '') payload.id = input.id;
      else throw new Error('Missing line');
      if (input.properties) payload.properties = input.properties;
      return postJSON('cart/change.js', payload).then(function (cart) {
        return refreshNow(cart);
      });
    });
  }

  function update(payload) {
    return enqueue(function () {
      return postJSON('cart/update.js', payload || {}).then(emitUpdated);
    });
  }

  function get() {
    return enqueue(getNow);
  }

  function refresh() {
    return enqueue(function () {
      return refreshNow();
    });
  }

  window.ThemeCart = {
    add: add,
    change: change,
    update: update,
    get: get,
    refresh: refresh
  };
})();
